import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import EmailIcon from "@material-ui/icons/Email";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import FolderIcon from "@material-ui/icons/Folder";
import SecurityIcon from "@material-ui/icons/Security";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
    if (action.type === "LOAD_USERS") {
      const users = action.payload;
      const newUsers = [];

      users.forEach((user) => {
        const userIndex = state.findIndex((u) => u.id === user.id);
        if (userIndex !== -1) {
          state[userIndex] = user;
        } else {
          newUsers.push(user);
        }
      });

      return [...state, ...newUsers];
    }

    if (action.type === "UPDATE_USERS") {
      const user = action.payload;
      const userIndex = state.findIndex((u) => u.id === user.id);

      if (userIndex !== -1) {
        state[userIndex] = user;
        return [...state];
      } else {
        return [user, ...state];
      }
    }

    if (action.type === "DELETE_USER") {
      const userId = action.payload;

      const userIndex = state.findIndex((u) => u.id === userId);
      if (userIndex !== -1) {
        state.splice(userIndex, 1);
      }
      return [...state];
    }

    if (action.type === "RESET") {
      return [];
    }
  };

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: "transparent",
    border: "none",
  },
  userCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  cardHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3, 2, 2),
    background: `linear-gradient(to bottom, ${theme.palette.primary.light}10, transparent)`,
  },
  largeAvatar: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
    border: `3px solid ${theme.palette.background.paper}`,
  },
  userName: {
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(0.5),
  },
  userRole: {
    textTransform: "capitalize",
    fontWeight: 500,
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  infoIcon: {
    fontSize: "1.2rem",
    color: theme.palette.primary.main,
  },
  cardActions: {
    justifyContent: "center",
    padding: theme.spacing(1.5),
    background: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  actionButton: {
    margin: theme.spacing(0, 0.5),
  }
}));

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      <MainHeader>
        <Title>{i18n.t("users.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenUserModal}
          >
            {i18n.t("users.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Grid container spacing={3}>
          {users.map((user) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
              <Card className={classes.userCard} variant="outlined">
                <div className={classes.cardHeader}>
                  <Avatar 
                    src={user.profileImage} 
                    alt={user.name} 
                    className={classes.largeAvatar}
                  >
                    {!user.profileImage && user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="h6" className={classes.userName}>
                    {user.name}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={user.profile} 
                    color={user.profile === 'admin' ? 'secondary' : 'default'}
                    className={classes.userRole}
                  />
                </div>
                
                <CardContent className={classes.cardContent}>
                  <div className={classes.infoRow}>
                    <EmailIcon className={classes.infoIcon} />
                    <Typography variant="body2" noWrap>
                      {user.email}
                    </Typography>
                  </div>
                  
                  <div className={classes.infoRow}>
                    <WhatsAppIcon className={classes.infoIcon} />
                    <Typography variant="body2" noWrap>
                      {user.profile === 'admin' 
                        ? "Acceso total"
                        : (user.allTicketConnections === "allowed" 
                          ? "Todas las conexiones"
                          : (user.whatsapps && user.whatsapps.length > 0 
                              ? user.whatsapps.map(w => w.name).join(", ")
                              : (user.whatsapp?.name || "Sin conexión asignada")
                            )
                          )
                      }
                    </Typography>
                  </div>

                  <div className={classes.infoRow}>
                    <FolderIcon className={classes.infoIcon} />
                    <Typography variant="body2" noWrap>
                      {user.profile === 'admin'
                        ? "Acceso total"
                        : (() => {
                            try {
                              const parsed = Array.isArray(user.assignedFolders) 
                                ? user.assignedFolders 
                                : JSON.parse(user.assignedFolders || "[]");
                              
                              if (parsed.length === 0) return "Sin asignaciones";

                              // Contar cuántos elementos son carpetas (números) y cuántos son etiquetas (strings que empiezan con sys_label_)
                              const foldersCount = parsed.filter(item => typeof item === 'number' || (typeof item === 'string' && !item.startsWith('sys_label_'))).length;
                              const labelsCount = parsed.filter(item => typeof item === 'string' && item.startsWith('sys_label_')).length;

                              let displayText = [];
                              if (foldersCount > 0) {
                                displayText.push(`${foldersCount} carpeta${foldersCount > 1 ? 's' : ''}`);
                              }
                              if (labelsCount > 0) {
                                displayText.push(`${labelsCount} etiqueta${labelsCount > 1 ? 's' : ''}`);
                              }

                              return displayText.join(" y ");
                            } catch (e) {
                              return "Sin asignaciones";
                            }
                          })()
                      }
                    </Typography>
                  </div>

                  <div className={classes.infoRow}>
                    <SecurityIcon className={classes.infoIcon} />
                    <Typography variant="body2" noWrap>
                      {user.profile === 'admin' 
                        ? "Acceso total" 
                        : (user.permissions 
                            ? `${Object.keys(typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions).filter(k => (typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions)[k]).length} módulos permitidos`
                            : "Sin módulos"
                          )
                      }
                    </Typography>
                  </div>
                </CardContent>

                <CardActions className={classes.cardActions}>
                  <Tooltip title={i18n.t("users.buttons.edit")}>
                    <IconButton
                      size="small"
                      color="primary"
                      className={classes.actionButton}
                      onClick={() => handleEditUser(user)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={i18n.t("users.buttons.delete")}>
                    <IconButton
                      size="small"
                      color="secondary"
                      className={classes.actionButton}
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingUser(user);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {loading && (
            <Grid item xs={12}>
              <TableRowSkeleton columns={4} />
            </Grid>
          )}
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default Users;
